import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './Login.css';

const USER_SERVICE_URL = process.env.REACT_APP_USER_SERVICE_URL || 'http://localhost:8090';
const CONFERENCE_SERVICE_URL = process.env.REACT_APP_CONFERENCE_API_URL || 'http://localhost:8080/conference';

const Login = ({ onLoginSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'ADMIN'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const roles = ['ADMIN', 'CHAIR', 'REVIEWER'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let response;
      let data;

      // Trim credentials
      const trimmedEmail = formData.email?.trim();
      const trimmedPassword = formData.password?.trim();

      if (formData.role === 'ADMIN') {
        // Admin authentication via admin_user table
        response = await fetch(`${USER_SERVICE_URL}/admin/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: trimmedEmail,
            password: trimmedPassword
          })
        });

        if (!response.ok) {
          let errorMessage = 'Login failed';
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch (parseError) {
            errorMessage = `Server error (${response.status}): ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }

        data = await response.json();
      } else {
        // CHAIR/REVIEWER authentication via submission_reviewers table
        response = await fetch(`${CONFERENCE_SERVICE_URL}/api/submission-reviewers/email/${encodeURIComponent(trimmedEmail)}`);

        if (!response.ok) {
          throw new Error('Invalid email or password');
        }

        const user = await response.json();

        // Verify role matches
        if (user.role !== formData.role) {
          throw new Error(`Invalid credentials for ${formData.role} role`);
        }

        // Verify password
        if (user.password !== trimmedPassword) {
          throw new Error('Invalid email or password');
        }

        // Create user data object similar to admin response
        data = {
          id: user.id,
          email: user.email,
          fullName: user.fullName,
          role: user.role,
          firstLogin: false
        };
      }
      
      // Store user info in localStorage
      localStorage.setItem('adminUser', JSON.stringify(data));
      
      // Call the onLoginSuccess callback
      if (onLoginSuccess) {
        onLoginSuccess(data);
      }

      // Redirect based on firstLogin flag and role
      if (data.firstLogin) {
        navigate('/change-password');
      } else if (data.role === 'ADMIN') {
        navigate('/analytics');
      } else {
        navigate('/my-reviews'); // Redirect reviewers/chairs to their review page
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <h1>Admin Portal</h1>
          <p>Sign in to continue</p>
        </div>

        {error && (
          <div className="alert alert-error">
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="role">Login As</label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              {roles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email"
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter your password"
              required
            />
          </div>

          <button 
            type="submit" 
            className="btn btn-primary btn-block"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={{ marginTop: '15px', textAlign: 'center' }}>
          <Link to="/forgot-password" className="link-primary" style={{ fontSize: '14px' }}>
            Forgot Password?
          </Link>
        </div>

        <div className="login-footer">
          <p>© 2025 GHA Conference Admin</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
